import { Alert, CircularProgress, Stack } from '@mui/material';
import { Suspense, lazy, type ComponentType } from 'react';
import { useIntl } from 'react-intl';
import { Navigate, useSearchParams } from 'react-router';

import { createAuthorizationHeaders } from '../../api/httpClient';
import RemoteBoundary from '../../components/AppLayout/RemoteBoundary';
import { withTdiConnectVariants } from '../../config';
import { useUserData } from '../../redux/selectors';

interface OnboardingApiConfig {
  authV3Base: string;
  userEmail: string;
  getAuthHeaders?: () => Promise<HeadersInit | undefined>;
}

interface AdminReviewPanelProps {
  apiConfig?: OnboardingApiConfig;
  requestEmail?: string;
  locale?: string;
}

const AdminReviewPanel = lazy<ComponentType<AdminReviewPanelProps>>(
  () => import('onboardingMicrofrontend/AdminReviewPanel')
);

const OnboardingAdminReview = (): React.JSX.Element => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const user = useUserData();
  const authV3Base = import.meta.env.VITE_AUTH_V3_BASE;

  const adminRoles = withTdiConnectVariants(['ADMIN']);
  const normalizedRoles = (user.roles ?? []).map(role => role.trim().toUpperCase());
  const isAdmin = normalizedRoles.some(role => adminRoles.includes(role));

  // Wait for user bootstrap before making an authorization decision.
  if (!user._id) {
    return (
      <Stack
        alignItems='center'
        padding={4}
      >
        <CircularProgress />
      </Stack>
    );
  }

  if (!isAdmin) {
    return (
      <Navigate
        to='/404'
        replace
      />
    );
  }

  const apiConfig = {
    authV3Base,
    userEmail: user.email,
    getAuthHeaders: createAuthorizationHeaders,
  };

  const requestEmail = searchParams.get('email') ?? undefined;

  return (
    <RemoteBoundary
      name='AdminReviewPanel'
      microfrontend='onboarding-mf'
      fallback={
        <Alert severity='warning'>{intl.formatMessage({ id: 'onboardingUnavailable' })}</Alert>
      }
    >
      <Suspense
        fallback={
          <Stack
            alignItems='center'
            padding={4}
          >
            <CircularProgress />
          </Stack>
        }
      >
        <AdminReviewPanel
          apiConfig={apiConfig}
          locale={user.locale ?? 'en'}
          {...(requestEmail ? { requestEmail } : {})}
        />
      </Suspense>
    </RemoteBoundary>
  );
};

export default OnboardingAdminReview;
